// Throttle/brake touch zones, plus keyboard controls for desktop development.

export interface PedalState {
  throttle: number;
  brake: number;
}

/**
 * Hold the right side of the screen for throttle, the left side for brake.
 * The centre strip is left free (it holds the speedo / re-centre button).
 */
export class TouchPedals {
  private readonly pointers = new Map<number, 'throttle' | 'brake'>();

  constructor(
    private readonly el: HTMLElement,
    /** Maps a pointer to game-frame x and the game width (the game may be drawn rotated). */
    private readonly toGameX: (e: PointerEvent) => { x: number; width: number } = (e) => ({ x: e.clientX, width: window.innerWidth }),
    private readonly zoneFraction = 0.42,
  ) {
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onUp);
    el.addEventListener('lostpointercapture', this.onUp);
  }

  get state(): PedalState {
    let throttle = 0;
    let brake = 0;
    for (const zone of this.pointers.values()) {
      if (zone === 'throttle') throttle = 1;
      else brake = 1;
    }
    return { throttle, brake };
  }

  get active(): boolean {
    return this.pointers.size > 0;
  }

  private zoneAt(e: PointerEvent): 'throttle' | 'brake' | undefined {
    const { x, width } = this.toGameX(e);
    if (x >= width * (1 - this.zoneFraction)) return 'throttle';
    if (x <= width * this.zoneFraction) return 'brake';
    return undefined;
  }

  private readonly onDown = (e: PointerEvent): void => {
    const zone = this.zoneAt(e);
    if (!zone) return;
    e.preventDefault();
    this.el.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, zone);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    // Sliding a thumb across the screen switches pedal; sliding into the centre releases it.
    const zone = this.zoneAt(e);
    if (zone) this.pointers.set(e.pointerId, zone);
    else this.pointers.delete(e.pointerId);
  };

  private readonly onUp = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId);
  };
}

export class KeyboardControls {
  private readonly down = new Set<string>();
  private steer = 0;
  used = false;
  onRecentre: (() => void) | undefined;
  onToggleHorizon: (() => void) | undefined;
  onPause: (() => void) | undefined;
  onBox: (() => void) | undefined;
  /** Wheel mode: E / Q shift up and down (paddles not bound, or testing without a wheel). */
  onShiftUp: (() => void) | undefined;
  onShiftDown: (() => void) | undefined;

  constructor() {
    window.addEventListener('keydown', (e) => {
      this.used = true;
      this.down.add(e.code);
      if (e.code === 'KeyC') this.onRecentre?.();
      if (e.code === 'KeyH') this.onToggleHorizon?.();
      if ((e.code === 'Escape' || e.code === 'KeyP') && !e.repeat) this.onPause?.();
      if (e.code === 'KeyB' && !e.repeat) this.onBox?.();
      if (e.code === 'KeyE' && !e.repeat) this.onShiftUp?.();
      if (e.code === 'KeyQ' && !e.repeat) this.onShiftDown?.();
    });
    window.addEventListener('keyup', (e) => this.down.delete(e.code));
    window.addEventListener('blur', () => this.down.clear());
  }

  private any(...codes: string[]): boolean {
    return codes.some((c) => this.down.has(c));
  }

  get pedals(): PedalState {
    return {
      throttle: this.any('ArrowUp', 'KeyW') ? 1 : 0,
      brake: this.any('ArrowDown', 'KeyS') ? 1 : 0,
    };
  }

  /** Keyboard steering ramps towards full lock so it isn't purely digital. */
  updateSteer(dt: number): number {
    const target = (this.any('ArrowRight', 'KeyD') ? 1 : 0) - (this.any('ArrowLeft', 'KeyA') ? 1 : 0);
    const rate = target === 0 ? 6 : 3;
    const delta = target - this.steer;
    this.steer += Math.sign(delta) * Math.min(Math.abs(delta), rate * dt);
    return this.steer;
  }
}
